import { create } from 'zustand';

/**
 * The prototype's toast: bottom center, dark pill, white 13px/500, optional
 * underlined action, auto-dismiss after 4 s (longer when asked: an
 * instruction takes longer to read than a count). One at a time; a new toast
 * replaces the old one and restarts the timer.
 *
 * `lift` raises it to 90px while the selection toolbar is showing (the
 * toolbar sets it; see SelectionToolbar).
 */
export interface ToastAction {
  label: string;
  fn: () => void;
}

interface ToastState {
  msg: string | null;
  action: ToastAction | null;
  lift: boolean;
  show: (msg: string, action?: ToastAction, ms?: number) => void;
  hide: () => void;
  setLift: (lift: boolean) => void;
}

let timer: ReturnType<typeof setTimeout> | undefined;

export const useToast = create<ToastState>((set) => ({
  msg: null,
  action: null,
  lift: false,
  show: (msg, action, ms = 4000) => {
    clearTimeout(timer);
    set({ msg, action: action ?? null });
    timer = setTimeout(() => set({ msg: null, action: null }), ms);
  },
  hide: () => {
    clearTimeout(timer);
    set({ msg: null, action: null });
  },
  setLift: (lift) => set({ lift }),
}));

/** Imperative helper for stores and handlers outside React. */
export function toast(msg: string, action?: ToastAction, ms?: number): void {
  useToast.getState().show(msg, action, ms);
}

export function ToastHost() {
  const { msg, action, lift, hide } = useToast();
  if (!msg) return null;
  return (
    <div
      style={{
        position: 'fixed',
        bottom: lift ? 90 : 24,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        height: 40,
        padding: '0 14px 0 16px',
        borderRadius: 8,
        background: 'var(--tr-ink)',
        color: 'var(--tr-on-ink)',
        fontSize: 13,
        fontWeight: 500,
        boxShadow: '0 8px 24px rgba(0,0,0,.2)',
        zIndex: 50,
        whiteSpace: 'nowrap',
      }}
    >
      {msg}
      {action ? (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            action.fn();
            hide();
          }}
          style={{ color: 'var(--tr-on-ink)', textDecoration: 'underline', textUnderlineOffset: 3 }}
        >
          {action.label}
        </a>
      ) : null}
    </div>
  );
}
